<template>
  <div
    v-editable="blok"
    class="PromoGrid layout-container"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div
      v-if="blok.headingTitle || blok.headingButtonText"
      class="flex flex-row flex-wrap justify-between items-center gap-8 mb-16"
      :class="{'flex-col text-center' : blok.headingAlign === 'center'}"
    >
      <div class="type-headline-2xl"> {{ blok.headingTitle }}</div>

      <Nuxt-link
        :to="$sbLinkFix(blok.headingButtonLink)"
        :target="$sbLinkTarget(blok.headingButtonLink)"
      >
        <div
          v-if="blok.headingButtonText"
          class="btn btn--primary"
          :class="{
            'btn--secondary': blok.headingButtonStyle === 'secondary',
            'btn--underline': blok.headingButtonStyle === 'underline',
          }"
        >
          {{ blok.headingButtonText }}
        </div>
      </Nuxt-link>
    </div>
    <div
      v-if="(blok.mobileSlider && isMobile) || (featureCount > 4)"
      class="relative"
    >
      <client-only>
        <Swiper
          class="swiper-cover desk:!overflow-hidden !overflow-visible w-full desk:!pb-40 !pb-32"
          :modules="[SwiperNavigation, SwiperPagination, SwiperA11y]"
          :space-between="uiStore.isMobile ? 8 : 12"
          :pagination="{
            clickable: true,
            dynamicBullets: true,
          }"
          :navigation="{
            nextEl: `.swiper-button-next.swiper-for-blok-${blok._uid}`,
            prevEl: `.swiper-button-prev.swiper-for-blok-${blok._uid}`,
          }"

          :loop="false"
          :slides-per-view="uiStore.isMobile ? 1.1 : 4"
          :no-swiping="true"
        >
          <SwiperSlide
            v-for="(feature, index) in blok.content"
            :key="feature._uid"
          >
            <component
              :is="feature.component"
              :key="feature._uid"
              :aspect-ratio-mobile="blok.aspectRatioMobile"
              :aspect-ratio-desktop="blok.aspectRatioDesktop"
              :blok="feature"
              :placement="placement"
              :size="promoSize(index+1)"
              :inside-grid="true"
              class="mb-24 desk:mb-32"
              :class="`promo${index+1}`"
            />
          </SwiperSlide>
        </Swiper>
      </client-only>

      <div class="hidden desk:block">
        <div
          class="swiper-button-prev !top-[158px]"
          :class="{[`swiper-for-blok-${blok._uid}`]: true}"
        />
        <div
          class="swiper-button-next !top-[158px]"
          :class="{[`swiper-for-blok-${blok._uid}`]: true}"
        />
      </div>
    </div>

    <div v-else>
      <!-- Default Layout: auto-fit columns based on promo count -->
      <div
        v-if="blok.layout === 'default' || ((blok.layout === 'firstLarge' || blok.layout === 'lastLarge') && blok.content.length > 3)"
        class="grid gap-x-8 gap-y-24 mobOnly:grid-cols-1"
        :class="`grid-cols-[repeat(auto-fit,_minmax(220px,_1fr))] desk:grid-cols-[${blok.content.length}]`"
      >
        <component
          :is="promo.component"
          v-for="(promo, index) in blok.content"
          :key="promo._uid"
          :aspect-ratio-mobile="blok.aspectRatioMobile"
          :aspect-ratio-desktop="blok.aspectRatioDesktop"
          :blok="promo"
          :placement="placement"
          :size="promoSize(index+1)"
          :inside-grid="true"
          class="mobOnly:mb-24"
          :class="`promo${index+1}`"
        />
      </div>

      <!-- First Large: only render if there are 3 or fewer items -->
      <div
        v-else-if="blok.layout === 'firstLarge' && blok.content.length <= 3"
        class="grid grid-cols-1 desk:grid-cols-2 gap-x-8 gap-y-24"
      >
        <div>
          <component
            :is="blok.content[0].component"
            :blok="blok.content[0]"
            :aspect-ratio-mobile="blok.aspectRatioMobile"
            :aspect-ratio-desktop="blok.aspectRatioDesktop"
            :placement="placement"
            :size="promoSize(0)"
            :inside-grid="true"
            class="mobOnly:mb-24 promo1"
          />
        </div>
        <div
          class="grid gap-x-8 gap-y-24"
          :class="`grid-cols-[repeat(auto-fit,_minmax(234px,_1fr))] desk:grid-cols-[${blok.content.length}]`"
        >
          <component
            :is="promo.component"
            v-for="(promo, index) in blok.content.slice(1)"
            :key="promo._uid"
            :aspect-ratio-mobile="blok.aspectRatioMobile"
            :aspect-ratio-desktop="blok.aspectRatioDesktop"
            :blok="promo"
            :placement="placement"
            :size="promoSize(index+1)"
            :inside-grid="true"
            class="mobOnly:mb-24"
            :class="`promo${index+2}`"
          />
        </div>
      </div>

      <!-- Last Large: only render if there are 3 or fewer items -->
      <div
        v-else-if="blok.layout === 'lastLarge' && blok.content.length <= 3"
        class="grid grid-cols-1 desk:grid-cols-2 gap-x-8 gap-y-24"
      >
        <div
          class="grid gap-x-8 gap-y-24"
          :class="`grid-cols-[repeat(auto-fit,_minmax(234px,_1fr))] desk:grid-cols-[${blok.content.length}]`"
        >
          <component
            :is="promo.component"
            v-for="(promo, index) in blok.content.slice(0, blok.content.length - 1)"
            :key="promo._uid"
            :aspect-ratio-mobile="blok.aspectRatioMobile"
            :aspect-ratio-desktop="blok.aspectRatioDesktop"
            :blok="promo"
            :placement="placement"
            :size="promoSize(index+1)"
            :inside-grid="true"
            class="mobOnly:mb-24"
            :class="`promo${index+2}`"
          />
        </div>
        <div>
          <component
            :is="blok.content[blok.content.length - 1].component"
            :blok="blok.content[blok.content.length - 1]"
            :aspect-ratio-mobile="blok.aspectRatioMobile"
            :aspect-ratio-desktop="blok.aspectRatioDesktop"
            :placement="placement"
            :size="promoSize(0)"
            :inside-grid="true"
            class="mobOnly:mb-24 promo1"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import { PromoBlock, StoryBlokLink, StoryBlokPropBase } from '~/constants/types/storyblok';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { storeToRefs } from 'pinia';
import { useUiStore } from '~/store/ui';

import { AspectRatioKeys } from '~/constants/aspectRatioKeys';

const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);

type Props = StoryBlokPropBase & {
  content: PromoBlock[];
  mobileSlider: boolean;
  aspectRatioMobile: AspectRatioKeys;
  aspectRatioDesktop: AspectRatioKeys;
  // Heading fields
  headingTitle: string;
  headingButtonText: string;
  headingButtonLink: StoryBlokLink;
  headingButtonStyle: 'primary' | 'secondary' | 'underline';
  headingAlign: 'left' | 'center';
  layout: 'default' | 'firstLarge' | 'lastLarge';
}

const props = defineProps<{
  blok: Props,
  placement: number,
}>();

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

const featureCount = computed(() => props.blok.content.length);

const promoSize = () => {
  if (props.blok.content.length === 1) {
    return 'full';
  }
  if (props.blok.content.length === 2) {
    return 'half';
  }
  if (props.blok.content.length === 3) {
    return 'third';
  }
  return 'fourth';
};

</script>

<style scoped lang="postcss">
.swiper-button-next {
  @apply !-right-16;
}
.swiper-button-prev {
  @apply !-left-16;
}
</style>
